import { Card, CardContent, Grid } from "@mui/material"
import React, { ChangeEvent } from "react"
import { customPalette } from "../../../../../../../../../../theme"
import { FormNumberInput } from "../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { BooleanRadioButtonInput } from "../../../../../../../../../components/inputs/radio-button-input/BooleanRadioButtonInput"
import { FormSelectInput } from "../../../../../../../../../components/inputs/select-input/FormSelectInput"
import { BSBuildingMorphology } from "../../../../../../../../../core/dto/beem-shot/BSBuildingMorphology/BSBuildingMorphology"
import { BSBuildingMorphologyCreationDto } from "../../../../../../../../../core/dto/beem-shot/BSBuildingMorphology/BSBuildingMorphologyCreationDto"
import { BSBuildingMorphologyUpdateDto } from "../../../../../../../../../core/dto/beem-shot/BSBuildingMorphology/BSBuildingMorphologyUpdateDto"
import {
  BSBuildingMorphologyEnum,
  bsBuildingMorphologyLabelEnum,
} from "../../../../../../../../../core/enum/bsBuildingMorphologyEnum"
import { enumToSelectOptions } from "../../../../../../../../../core/services/helper-service"
import DividerSimplifiedQuestionnaire from "../../../../building/BSCalculationPage/BSCalculationForm/components/SimplifiedQuestionnaire/DividerSimplifiedQuestionnaire"
import { BSBuildingMorphologyDisplayer } from "./BSBuildingMorphologyDisplayer"

export interface IBSBuildingMorphologyForm {
  shape: BSBuildingMorphologyEnum
  dimensionRectangle1: number
  dimensionRectangle2: number
  dimensionL1: number
  dimensionL2: number
  dimensionL3: number
  dimensionL4: number

  ratioVitrageFacade: number
  ratioMursRideauxVitrage: number
  murRideauVitrage: boolean
  ratioVerrieresToiture: number
  verrieresToiture: boolean
}

export function bsBuildingMorphologyFormToCreationDto(
  form: IBSBuildingMorphologyForm,
  bsBuildingId: string
): BSBuildingMorphologyCreationDto {
  const rectangleDimension =
    form?.dimensionRectangle1 && form?.dimensionRectangle2
      ? { dimensionRectangle1: form?.dimensionRectangle1, dimensionRectangle2: form?.dimensionRectangle2 }
      : undefined

  const shapeLDimension =
    form?.dimensionL1 && form?.dimensionL2 && form?.dimensionL3 && form?.dimensionL4
      ? {
          dimensionL1: form?.dimensionL1,
          dimensionL2: form?.dimensionL2,
          dimensionL3: form?.dimensionL3,
          dimensionL4: form?.dimensionL4,
        }
      : undefined

  return {
    bsBuildingId,
    shape: form?.shape,
    rectangleDimension,
    shapeLDimension,

    ratioVitrageFacade: form?.ratioVitrageFacade ?? 0,
    ratioMursRideauxVitrage: form?.ratioMursRideauxVitrage ?? 0,
    murRideauVitrage: form?.murRideauVitrage ?? false,
    ratioVerrieresToiture: form?.ratioVerrieresToiture ?? 0,
    verrieresToiture: form?.verrieresToiture ?? false,
  }
}

export function bsBuildingMorphologyFormToUpdateDto(
  form: IBSBuildingMorphologyForm,
  bsBuildingId: string,
  bsBuildingMorphologyId: string
): BSBuildingMorphologyUpdateDto {
  const rectangleDimension =
    form?.dimensionRectangle1 && form?.dimensionRectangle2
      ? { dimensionRectangle1: form?.dimensionRectangle1, dimensionRectangle2: form?.dimensionRectangle2 }
      : undefined

  const shapeLDimension =
    form?.dimensionL1 && form?.dimensionL2 && form?.dimensionL3 && form?.dimensionL4
      ? {
          dimensionL1: form?.dimensionL1,
          dimensionL2: form?.dimensionL2,
          dimensionL3: form?.dimensionL3,
          dimensionL4: form?.dimensionL4,
        }
      : undefined

  return {
    id: bsBuildingMorphologyId,
    bsBuildingId,
    shape: form?.shape,
    rectangleDimension,
    shapeLDimension,

    ratioVitrageFacade: form?.ratioVitrageFacade ?? 0,
    ratioMursRideauxVitrage: form?.ratioMursRideauxVitrage ?? 0,
    murRideauVitrage: form?.murRideauVitrage ?? false,
    ratioVerrieresToiture: form?.ratioVerrieresToiture ?? 0,
    verrieresToiture: form?.verrieresToiture ?? false,
  }
}

export function bsBuildingMorphologyDtoToForm(dto: BSBuildingMorphology | undefined): IBSBuildingMorphologyForm {
  return dto
    ? {
        shape: dto.shape,
        dimensionRectangle1: dto.rectangleDimension?.dimensionRectangle1,
        dimensionRectangle2: dto.rectangleDimension?.dimensionRectangle2,
        dimensionL1: dto.shapeLDimension?.dimensionL1,
        dimensionL2: dto.shapeLDimension?.dimensionL2,
        dimensionL3: dto.shapeLDimension?.dimensionL3,
        dimensionL4: dto.shapeLDimension?.dimensionL4,

        ratioVitrageFacade: dto.ratioVitrageFacade,
        ratioMursRideauxVitrage: dto.ratioMursRideauxVitrage,
        murRideauVitrage: dto.murRideauVitrage,
        ratioVerrieresToiture: dto.ratioVerrieresToiture,
        verrieresToiture: dto.verrieresToiture,
      }
    : ({
        shape: BSBuildingMorphologyEnum.RECTANGLE,
        dimensionRectangle1: 0,
        dimensionRectangle2: 0,
        dimensionL1: 0,
        dimensionL2: 0,
        dimensionL3: 0,
        dimensionL4: 0,

        ratioVitrageFacade: 17,
        ratioMursRideauxVitrage: 0,
        murRideauVitrage: false,
        ratioVerrieresToiture: 0,
        verrieresToiture: false,
      } as IBSBuildingMorphologyForm)
}

interface IProps {
  form: IBSBuildingMorphologyForm
  errors: Record<string, string | undefined>
  handleChange(event: ChangeEvent<HTMLInputElement>): void
}

export function BSBuildingMorphologyForm({ form, errors, handleChange }: Readonly<IProps>): React.JSX.Element {
  return (
    <Card
      sx={{
        backgroundColor: customPalette.white,
        borderRadius: 3,
        mb: 3,
        boxShadow: "2px 4px 20px 0px rgba(0, 0, 0, 0.05)",
        width: "100%",
      }}>
      <CardContent>
        <Grid container>
          <Grid container item xs={6} sx={{ p: 1 }}>
            <Grid item xs={12}>
              <FormSelectInput
                id="shape"
                label="Forme du bâtiment"
                mode="event"
                selectedOption={form.shape}
                options={enumToSelectOptions(BSBuildingMorphologyEnum, bsBuildingMorphologyLabelEnum)}
                handleEventChange={handleChange}
                errors={errors}
              />
            </Grid>

            {form.shape === BSBuildingMorphologyEnum.RECTANGLE && (
              <Grid item xs={12}>
                <FormNumberInput
                  fieldName="dimensionRectangle1"
                  label="Dimension 1 (m)"
                  handleEventChange={handleChange}
                  form={form}
                  errors={errors}
                />
                <FormNumberInput
                  fieldName="dimensionRectangle2"
                  label="Dimension 2 (m)"
                  handleEventChange={handleChange}
                  form={form}
                  errors={errors}
                />
              </Grid>
            )}

            {form.shape === BSBuildingMorphologyEnum.L && (
              <Grid item xs={12}>
                <FormNumberInput
                  fieldName="dimensionL1"
                  label="Dimension 1 (m)"
                  handleEventChange={handleChange}
                  form={form}
                  errors={errors}
                />
                <FormNumberInput
                  fieldName="dimensionL2"
                  label="Dimension 2 (m)"
                  handleEventChange={handleChange}
                  form={form}
                  errors={errors}
                />
                <FormNumberInput
                  fieldName="dimensionL3"
                  label="Dimension 3 (m)"
                  handleEventChange={handleChange}
                  form={form}
                  errors={errors}
                />
                <FormNumberInput
                  fieldName="dimensionL4"
                  label="Dimension 4 (m)"
                  handleEventChange={handleChange}
                  form={form}
                  errors={errors}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <DividerSimplifiedQuestionnaire label="Enveloppe vitrée" />
            </Grid>

            <Grid item xs={12}>
              <FormNumberInput
                fieldName="ratioVitrageFacade"
                label="Ratio de vitrage / façade (%)"
                value={form.ratioVitrageFacade}
                formBackgroundColor={customPalette.white}
                handleEventChange={handleChange}
                errors={errors}
              />
            </Grid>

            <Grid item xs={12}>
              <BooleanRadioButtonInput
                id="murRideauVitrage"
                label="Murs rideaux"
                value={form.murRideauVitrage}
                handleChange={handleChange}
              />
            </Grid>
            {form.murRideauVitrage && (
              <Grid item xs={12}>
                <FormNumberInput
                  fieldName="ratioMursRideauxVitrage"
                  label="Part des murs rideaux de la surface totale vitrée (%)"
                  value={form.ratioMursRideauxVitrage}
                  formBackgroundColor={customPalette.white}
                  handleEventChange={handleChange}
                  errors={errors}
                />
              </Grid>
            )}

            {false && (
              <>
                <Grid item xs={12}>
                  <BooleanRadioButtonInput
                    id="verrieresToiture"
                    label="Verrières (de toiture)"
                    value={form.verrieresToiture}
                    handleChange={handleChange}
                  />
                </Grid>
                {form.verrieresToiture && (
                  <Grid item xs={12}>
                    <FormNumberInput
                      fieldName="ratioVerrieresToiture"
                      label="Part des verrières de la surface toiture (%)"
                      value={form.ratioVerrieresToiture}
                      formBackgroundColor={customPalette.white}
                      handleEventChange={handleChange}
                      errors={errors}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>

          <Grid item xs={6} display="flex" justifyContent="center" sx={{ p: 1 }}>
            <BSBuildingMorphologyDisplayer shape={form.shape} height="450px" />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
